<template>
  <v-container fluid>
    <v-row
      class="rounded elevation-3"
      style="background-color: white;">
      <v-col
        cols="12"
        class="d-flex flex-row justify-space-between align-center">
        <h2>
          ค้นหาสินค้า (WMS Location)
        </h2>
        <v-spacer></v-spacer>
        <v-switch
          v-model="searchByFile"
          inset
          class="mt-0"
          label="Search ด้วยการ import file"
          dense
          @change="onSwitchSearchType" />
      </v-col>
      <v-col
        v-if="searchByFile"
        offset-md="9"
        md="3"
        cols="12">
        <v-file-input
          v-model="selectedFile"
          :disabled="loading"
          label="Import ไฟล์ CSV"
          accept=".csv"
          color="secondary"
          outlined
          dense
          hide-details
          @click:clear="clearItem()"
          @focus="clearValidate"
          @change="validateCsv" />
        <div class="text-right mt-3">
          <v-btn
            text
            color="green darken-2"
            @click="dialogTemplate = true">
            <v-icon
              class="mr-2"
              color="green darken-2">
              mdi-file-download-outline
            </v-icon>
            TEMPLATE
          </v-btn>
          <img-modal-preview
            v-model="dialogTemplate"
            :img-src="require('@/assets/image/template-upload-search-location.png')" />
        </div>
      </v-col>
      <v-col
        v-if="!searchByFile"
        offset-md="6"
        md="3"
        cols="12">
        <v-autocomplete
          v-model="query.collection"
          :items="collections"
          item-text="name"
          item-value="id"
          label="Collection"
          color="secondary"
          outlined
          dense
          hide-details
          clearable
          :loading="loadingAttribute"
          :disabled="loading"
          @change="fetchPaginate(true)"
        />
      </v-col>
      <v-col
        v-if="!searchByFile"
        md="3"
        cols="12">
        <search-box
          v-model="search"
          placeholder="Search by code, name"
          label="Search"
          @on-search="fetchPaginate(true)" />
      </v-col>
      <v-dialog
        v-model="dialogImport"
        max-width="500px"
        @click:outside="onCloseCsvDialog">
        <v-card>
          <v-card-title>Upload a File</v-card-title>

          <v-card-text class="pb-0">
            <p
              v-if="csvValidateErrors.length"
              class="text-bold">
              Total {{ csvValidateErrors.length }} errors.
            </p>
            <div
              v-if="csvValidateErrors.length"
              class="csv-fail">
              <p
                v-for="(errText, index) in csvValidateErrors"
                :key="index"
                class="red--text">
                {{ errText }}
              </p>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="tempReadFieldFromImport.length"
              text
              @click="downloadCsvToCheck">
              Download
            </v-btn>
            <v-btn
              text
              @click="onCloseCsvDialog">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-col cols="12">
        <v-data-table
          class="elevation-1"
          :headers="headers"
          :items="items"
          :loading="loading"
          :items-per-page="-1"
          hide-default-footer
        >
          <template v-slot:item.product.photoUrls="{ item }">
            <div class="d-flex justify-center">
              <img
                width="60px"
                :src="item.product.photoUrls.length ? $convertImgix(`${item.product.photoUrls[0]}?w=128`) : noPicture"
                alt="">
            </div>
          </template>
          <template v-slot:item.sku.color="{ item }">
            <p
              v-if="item.sku"
              class="mb-0">
              {{ item.sku.color.name.toUpperCase() }} - {{ item.sku.size.name.toUpperCase() }}
            </p>
          </template>
          <template v-slot:item.qty="{ item }">
            <p
              v-if="item.sku"
              class="mb-0">
              {{ item.qty | showNumberFormat }}
            </p>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              class="mx-2"
              small
              dark
              color="secondary"
              @click="setAdjustmentData(item, 'adjustment')">
              แก้ไขจำนวน
            </v-btn>
            <v-btn
              class="mx-2"
              small
              dark
              color="indigo"
              @click="setAdjustmentData(item, 'move')">
              ย้าย
            </v-btn>
          </template>
          <template v-slot:no-data>
            <p class="mb-0">
              ไม่พบข้อมูล
            </p>
          </template>
        </v-data-table>
      </v-col>
      <v-col cols="12">
        <v-pagination
          v-model="query.page"
          color="secondary"
          :total-visible="7"
          :length="totalPages"
          :disabled="loading"
          @input="refresh" />
      </v-col>
      <adjustment-qty-dialog
        v-if="showDialog"
        :visible.sync="showDialog"
        :adjustment-data="adjustmentData"
        title="แก้ไขจำนวน"
        :location="adjustmentData.location"
        @confirm="onAdjustmentConfirm"
        @cancel="onAdjustmentCancel"
      />
      <move-location-dialog
        v-if="showDialogMove"
        :visible.sync="showDialogMove"
        :adjustment-data="adjustmentData"
        :location="adjustmentData.location"
        title="ย้ายสินค้า"
        @confirm="onMoveLocationConfirm"
        @cancel="onMoveLocationCancel"
      />
    </v-row>
  </v-container>
</template>

<script>
import NoPicture from '@/assets/image/no_picture_available.png'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import SearchBox from '@/components/SearchBox.vue'
import StockMappingProvider from '@/resources/StockMappingProvider'
import { ExportToCsv } from 'export-to-csv'
import ImgModalPreview from '@/components/ImgModalPreview.vue'
import AdjustmentQtyDialog from '../components/AdjustmentQtyDialog.vue'
import MoveLocationDialog from '../components/MoveLocationDialog.vue'
import { getMoveErrorMessage } from '../utils/MoveLocationResult'

const fileReader = new FileReader()
const ProductCollectionService = new ProductCollectionProvider()
const stockMappingService = new StockMappingProvider()

export default {
  components: {
    SearchBox,
    AdjustmentQtyDialog,
    MoveLocationDialog,
    ImgModalPreview
  },
  data () {
    return {
      loadingAttribute: true,
      loading: false,
      noPicture: NoPicture,
      headers: [
        {
          text: 'photo',
          align: 'center',
          value: 'product.photoUrls',
          sortable: false
        },
        {
          text: 'Name',
          value: 'product.name',
          sortable: false,
          width: '150px'
        },
        {
          text: 'Collection',
          value: 'collectionName',
          sortable: false,
          width: '150px'
        },
        {
          text: 'Code',
          value: 'code',
          sortable: false,
          width: '100px'
        },
        {
          text: 'Color - Size',
          value: 'sku.color',
          sortable: false
        },
        {
          text: 'Location',
          value: 'location.name',
          sortable: false
        },
        {
          text: 'qty',
          value: 'qty',
          align: 'center',
          sortable: false
        },
        { text: 'Actions', align: 'center', value: 'actions', sortable: false }
      ],
      items: [],
      totalPages: 0,
      totalItems: 0,
      query: {
        collection: null,
        page: 1,
        limit: 20
      },
      search: '',
      collections: [],
      showDialog: false,
      showDialogMove: false,
      adjustmentData: null,
      dialogImport: false,
      csvValidateErrors: [],
      selectedFile: null,
      tempReadFieldFromImport: [],
      searchByFile: false,
      dialogTemplate: false
    }
  },
  watch: {
    query: {
      handler () {
        this.updateRouteQuery()
      },
      deep: true
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    async init () {
      if (this.$route.query.page) {
        this.query.page = Number(this.$route.query.page)
      }

      if (this.$route.query.search) {
        this.search = this.$route.query.search
      }

      if (this.$route.query.collection) {
        this.query.collection = this.$route.query.collection
      }
      await this.getAttributes()
      this.fetchPaginate()
    },
    async getAttributes () {
      try {
        this.loadingAttribute = true
        const { data: collections } = await ProductCollectionService.getAll({
          page: 1,
          limit: 999,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        })
        this.collections = collections.results
      } catch (error) {
        console.error('getAttributes', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loadingAttribute = false
      }
    },
    async fetchPaginate (isClear = false) {
      if (isClear) {
        this.query.page = 1
        this.totalPages = 0
        this.totalItems = 0
      }

      try {
        this.loading = true

        const { data } = await stockMappingService.getMappingLocationPaginate({ ...this.query, search: this.search })

        this.items = data.results
        this.totalPages = data.pages
        this.totalItems = data.total
      } catch (error) {
        console.error('getMapping', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    refresh () {
      if (this.searchByFile) {
        this.searchFromCSV()
      } else {
        this.fetchPaginate()
      }
    },
    async onAdjustmentConfirm (qty) {
      try {
        await stockMappingService.setMappingQty({
          mappingId: this.adjustmentData.id,
          qty
        })
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'แก้ไขจำนวนสำเร็จ',
          type: 'success'
        })
        this.refresh()
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      }
      this.adjustmentData = null
      this.showDialog = false
    },
    onAdjustmentCancel () {
      this.adjustmentData = null
      this.showDialog = false
    },
    setAdjustmentData (val, action) {
      this.adjustmentData = val
      if (action === 'adjustment') {
        this.showDialog = true
      } else {
        this.showDialogMove = true
      }
    },
    updateRouteQuery () {
      const newQuery = {
        page: this.query.page || 1,
        search: this.search || undefined,
        collection: this.query.collection || undefined
      }

      if (JSON.stringify(this.$route.query) !== JSON.stringify(newQuery)) {
        this.$router.replace({ query: newQuery }).catch((err) => {
          if (
            err.name !== 'NavigationDuplicated'
            && !err.message.includes('Avoided redundant navigation')
          ) {
            throw err
          }
        })
      }
    },
    async onMoveLocationConfirm (payload) {
      try {
        const { data } = await stockMappingService.moveLocation(payload)
        const errorMessage = getMoveErrorMessage(data)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: errorMessage || 'ย้ายสินค้าสำเร็จ',
          type: errorMessage ? 'error' : 'success'
        })
        this.refresh()
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      }
      this.adjustmentData = null
      this.showDialogMove = false
    },
    onMoveLocationCancel () {
      this.adjustmentData = null
      this.showDialogMove = false
    },
    onCloseCsvDialog () {
      this.dialogImport = false
      this.clearItem()
      this.clearValidate()
    },
    clearValidate () {
      this.selectedFile = null
      this.csvValidateErrors = []
    },
    validateCsv () {
      this.csvValidateErrors = []
      this.tempReadFieldFromImport = []
      if (!this.selectedFile) return
      fileReader.readAsText(this.selectedFile, 'utf8')
      fileReader.onload = async (readerEvent) => {
        try {
          const contents = readerEvent.target.result.split('\n')
          const elements = []
          contents.forEach((line) => {
            if (line.trim().length > 0) {
              const text = line
                .replaceAll('"', '')
                .replaceAll('\r', '')
                .split(',')
              if (!text.every((col) => col.trim() === '')) {
                elements.push(text)
              }
            }
          })
          if (!elements || !Array.isArray(elements) || !elements.length || elements.length === 1) {
            this.csvValidateErrors.push('The CSV contains no content. Please ensure the file has valid data.')
            return
          }

          const codeFields = elements[0].filter((h) => h.toLowerCase() === 'code')
          const headerFields = elements[0].filter((h) => h.toLowerCase())

          if (codeFields.length > 1) {
            this.csvValidateErrors.push('Header : คอลัมน์ code ซ้ำกรุณาตรวจสอบ')
            this.dialogImport = true
            return
          }

          if (codeFields.length === 0) {
            this.csvValidateErrors.push('Header : ไม่พบคอลัมน์ code ในไฟล์ กรุณาตรวจสอบ')
            this.dialogImport = true
            return
          }

          if (codeFields.length === 1 && !this.csvValidateErrors.length) {
            for (const [index, element] of elements.entries()) {
              if (index !== 0) {
                const row = Object.fromEntries(headerFields.map((key, mapIndex) => [key.toLowerCase(), element[mapIndex]]))
                let errMsg = ''
                if (!row.code) {
                  errMsg += `Row ${index + 1} : ไม่มีข้อมูล \n`
                } else if (!(/^(GW|MM|LW|GU)\./.test(row.code))) {
                  errMsg += `Row ${index + 1} : ไม่ใช่ code ของสินค้า \n`
                }
                this.tempReadFieldFromImport.push({ code: row.code || '', status: errMsg ? 'ERROR' : '' })
                if (errMsg) {
                  this.csvValidateErrors.push(errMsg)
                }
              }
            }
          }
        } catch (e) {
          this.csvValidateErrors.push('เกิดข้อผิดพลาด')
        }
        if (this.csvValidateErrors.length) {
          this.dialogImport = true
        } else {
          this.searchFromCSV()
        }
      }
    },
    async searchFromCSV () {
      try {
        this.loading = true
        // by-codes ใช้ PaginateDto (IsNumberString) → page/limit ต้องเป็น string
        const { data } = await stockMappingService.getLocationMappingPaginateByCodes({
          limit: `${this.query.limit}`,
          page: `${this.query.page}`,
          codes: [...new Set(this.tempReadFieldFromImport.map((v) => v.code))],
          sortBy: 'name',
          sortOrder: 'asc'
        })

        this.items = data.results
        this.totalPages = data.pages
        this.totalItems = data.total
      } catch (error) {
        console.error('getMappingByCodes', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
        this.dialogImport = false
      }
    },
    clearItem () {
      this.query.page = 1
      this.totalPages = 0
      this.totalItems = 0
      this.items = []
      this.selectedFile = null
      this.tempReadFieldFromImport = []
    },
    onSwitchSearchType (isSearchByFile) {
      this.clearItem()
      if (!isSearchByFile) {
        this.fetchPaginate()
      } else {
        this.query.collection = null
        this.search = ''
      }
    },
    async downloadCsvToCheck () {
      const options = {
        filename: 'check_error_codes',
        showLabels: true,
        useKeysAsHeaders: true
      }
      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(this.tempReadFieldFromImport)
    }
  }
}
</script>

<style scoped>
img {
  max-height: 80px;
}
.csv-fail {
  max-height: 200px;
  overflow-y: scroll;
  border: 2px solid red;
  padding: 12px;
}
</style>
